import { DesktopOutlined, QrcodeOutlined, SaveOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  ColorPicker,
  Divider,
  Form,
  message,
  QRCode,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import type { Color } from 'antd/es/color-picker';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FRONTEND_URL } from '../config/api';
import { raffleErrorKey, raffleGet, rafflePatch } from '../config/raffleApi';
import ImageUpload from './ImageUpload';
import SponsorLogoTable from './SponsorLogoTable';

const { Title, Text, Paragraph } = Typography;

/**
 * Raffle settings and the technician's corner (#92 point 5).
 *
 * The two URLs at the bottom are the reason this screen is not just a form.
 * On the day, somebody has to put the Stage Screen on an LED wall and a join QR
 * on a slide, usually minutes before the doors open and usually not the person
 * who built the round. Both live here, next to each other, copyable — so the
 * answer to "what do I open on the wall?" is one screen, not a chase through
 * the codebase for a URL shape.
 */

interface RaffleEventSettings {
  allowMultipleWins: boolean;
  stageLogoUrl?: string;
  stageBackgroundUrl?: string;
  stagePrimaryColor?: string;
  stageSponsorLogos?: string[];
  winnerColor?: string;
}

interface RaffleSettingsDraw {
  id: string;
  name: string;
  registrationToken: string;
}

/**
 * A branding field as the PATCH has to carry it.
 *
 * `undefined` is not "clear this" on this endpoint, it is "do not touch this".
 * Axios omits `undefined` keys entirely, and the backend merges what arrives
 * with a spread (`raffle: { ...stored.raffle, ...dto }` in `raffles.service.ts`),
 * so an absent key keeps the stored value — emptying the logo field would
 * appear to work and the old logo would still be on the LED wall after a
 * reload. An empty string is a key the merge actually overwrites, and the DTO
 * accepts it (`@IsOptional() @IsString()` skips only null/undefined).
 *
 * Nothing renders that empty string: the stage snapshot maps it back to
 * `undefined` (`configString` in `raffle-public.service.ts`) and `StageFrame`
 * falls back to no logo, no background and the house accent — so this is a
 * cleared field, not a blank `<img>`.
 */
const brandingField = (value?: string): string => value?.trim() ?? '';

/**
 * What the winner's phone shows when the organiser picks nothing (#176). Kept
 * in step with `DEFAULT_WINNER_COLOR` in the frontend's `lib/raffleTheme.ts` —
 * here it is only the hint in the picker.
 */
const DEFAULT_WINNER_COLOR = '#15803d';

interface RaffleSettingsProps {
  eventId: string;
  /** The Stage Screen is addressed by slug, not by id. */
  eventSlug?: string;
  draws: RaffleSettingsDraw[];
}

const RaffleSettings: React.FC<RaffleSettingsProps> = ({ eventId, eventSlug, draws }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<RaffleEventSettings>();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sponsorLogos, setSponsorLogos] = useState<string[]>([]);
  const [qrDrawId, setQrDrawId] = useState<string | undefined>();

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await raffleGet<RaffleEventSettings>(`/raffle/events/${eventId}/settings`);
      form.setFieldsValue({
        // The documented default is NO — an absent flag must not read as "on".
        allowMultipleWins: data.allowMultipleWins ?? false,
        stageLogoUrl: data.stageLogoUrl,
        stageBackgroundUrl: data.stageBackgroundUrl,
        stagePrimaryColor: data.stagePrimaryColor,
        winnerColor: data.winnerColor,
      });
      setSponsorLogos(data.stageSponsorLogos ?? []);
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    } finally {
      setLoading(false);
    }
  }, [eventId, form, t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    // Default the QR to the first round that exists, so the technician does not
    // land on an empty picker.
    setQrDrawId((current) => current ?? draws[0]?.id);
  }, [draws]);

  const handleSubmit = async (values: RaffleEventSettings) => {
    setSaving(true);
    try {
      await rafflePatch(`/raffle/events/${eventId}/settings`, {
        ...values,
        // See `brandingField`: an emptied field has to travel as "", because
        // the key the merge never sees is the key it never changes.
        stageLogoUrl: brandingField(values.stageLogoUrl),
        stageBackgroundUrl: brandingField(values.stageBackgroundUrl),
        stagePrimaryColor: brandingField(values.stagePrimaryColor),
        winnerColor: brandingField(values.winnerColor),
        stageSponsorLogos: sponsorLogos.filter(Boolean),
      });
      message.success(t('raffle.settings.saved'));
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    } finally {
      setSaving(false);
    }
  };

  const selectedDraw = draws.find((draw) => draw.id === qrDrawId);
  const stageUrl = eventSlug ? `${FRONTEND_URL}/raffle/stage/${eventSlug}` : '';
  const joinUrl = selectedDraw ? `${FRONTEND_URL}/raffle/${selectedDraw.registrationToken}` : '';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title={t('raffle.settings.title')} loading={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="allowMultipleWins"
            label={t('raffle.settings.allowMultipleWins')}
            tooltip={t('raffle.settings.allowMultipleWinsHint')}
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren={t('common.yes')} unCheckedChildren={t('common.no')} />
          </Form.Item>

          <Divider orientation="left">{t('raffle.settings.branding')}</Divider>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name="stageLogoUrl" label={t('raffle.settings.stageLogo')}>
                <ImageUpload
                  uploadUrl="/api/upload/logo"
                  fieldName="logo"
                  maxSize={2}
                  allowUrlInput
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="stageBackgroundUrl" label={t('raffle.settings.stageBackground')}>
                <ImageUpload
                  uploadUrl="/api/upload/image"
                  fieldName="image"
                  maxSize={5}
                  allowUrlInput
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="stagePrimaryColor"
            label={t('raffle.settings.primaryColor')}
            // The backend stores a string, but ColorPicker hands its own Color
            // object to `onChange`. Normalising here keeps a `[object Object]`
            // out of the event config.
            getValueFromEvent={(color: Color) => color?.toHexString()}
          >
            <ColorPicker showText format="hex" />
          </Form.Item>

          {/*
            The winner's phone (#176). Empty means the product's green, and the
            picker says so in words rather than showing a blank swatch that
            reads as "no colour". Clearing it sends the empty string back.
          */}
          <Form.Item
            name="winnerColor"
            label={t('raffle.settings.winnerColor')}
            tooltip={t('raffle.settings.winnerColorHint')}
            getValueFromEvent={(color: Color) => (color?.cleared ? '' : color?.toHexString())}
          >
            <ColorPicker
              format="hex"
              allowClear
              // The phone draws the colour solid (alpha is dropped), so the admin
              // must not offer a see-through swatch it will never show.
              disabledAlpha
              showText={(color) =>
                color.cleared
                  ? t('raffle.settings.winnerColorDefault', { color: DEFAULT_WINNER_COLOR })
                  : color.toHexString()
              }
            />
          </Form.Item>

          <Form.Item label={t('raffle.settings.sponsorLogos')}>
            <SponsorLogoTable logos={sponsorLogos} onChange={setSponsorLogos} />
          </Form.Item>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={() => form.submit()}
          >
            {t('common.save')}
          </Button>
        </Form>
      </Card>

      <Card title={t('raffle.stage.title')}>
        <Paragraph type="secondary">{t('raffle.stage.hint')}</Paragraph>

        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <Title level={5}>
              <Space>
                <DesktopOutlined />
                {t('raffle.stage.stageScreen')}
              </Space>
            </Title>
            {stageUrl ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text copyable={{ text: stageUrl }} code>
                  {stageUrl}
                </Text>
                <Space>
                  <Button type="primary" href={stageUrl} target="_blank" rel="noreferrer">
                    {t('raffle.stage.open')}
                  </Button>
                </Space>
                <QRCode value={stageUrl} size={148} />
              </Space>
            ) : (
              <Alert type="warning" showIcon message={t('raffle.stage.noSlug')} />
            )}
          </Col>

          <Col xs={24} md={12}>
            <Title level={5}>
              <Space>
                <QrcodeOutlined />
                {t('raffle.stage.joinQr')}
              </Space>
            </Title>
            {draws.length === 0 ? (
              <Alert type="info" showIcon message={t('raffle.stage.noDraws')} />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Select
                  style={{ width: '100%', maxWidth: 320 }}
                  value={qrDrawId}
                  onChange={setQrDrawId}
                  options={draws.map((draw) => ({
                    value: draw.id,
                    label: draw.name,
                  }))}
                />
                {joinUrl && (
                  <>
                    <Text copyable={{ text: joinUrl }} code>
                      {joinUrl}
                    </Text>
                    {/* This is the code the room scans — big enough to be
                        photographed off the organizer's laptop if a slide
                        goes missing. */}
                    <QRCode value={joinUrl} size={200} />
                  </>
                )}
              </Space>
            )}
          </Col>
        </Row>
      </Card>
    </Space>
  );
};

export default RaffleSettings;
